// 료칸 식당: 목욕탕 문으로 들어가는 실내 세트(다다미, 창호지 벽, 설경 창, 도코노마, 등), 식당 손님 3마리, 들어가기·나오기.
// 프로토타입 754–839 (세트 본문은 거의 그대로), 손님 2132–2136·think 1898–1901, goIn·enterRoom·exitRoom 3846–3872, 문 확인 3979–3983.
import * as THREE from 'three';
import { MENUS, type DishId } from '@capy/shared';
import { S, mk } from '../../engine/geometry';
import { plush } from '../../engine/materials';
import { LEGACY } from '../../engine/lights';
import { rand } from '../../engine/math';
import type { FollowCamera } from '../../engine/camera';
import { makeAgent } from '../../capy/agent';
import { setHead } from '../../capy/equip';
import { shakeOff, hopDown } from '../../capy/stacking';
import { cleanupEating, serve } from '../../food/eating';
import type { FurTone } from '../../capy/model';
import { asQuiet, releaseSlot, releaseWait, type QuietCapy, type QuietRules } from './bots';

/** 식당 세트는 온천에서 멀리 떨어진 x = 300에 둔다. */
const X0 = 300;
/** 내 자리와 음식이 놓이는 상 위 자리. */
export const ROOM = { seat: new THREE.Vector3(X0, 0, -1.6), dishSpot: new THREE.Vector3(X0, 0.53, 0.1) };
const DINER_SEATS = ([[-3.9, -2.4], [3.9, -2.4], [-3.9, 1.4]] as const).map(([x, z]) => ({ seat: new THREE.Vector3(X0 + x, 0, z), dishSpot: new THREE.Vector3(X0 + x, 0.53, z + 1.7) }));
/** 온천 쪽 목욕탕 문 앞. */
export const DOOR = new THREE.Vector3(0, 0, -9.7);

const canvasTex = (w: number, h: number, draw: (x: CanvasRenderingContext2D, w: number, h: number) => void) => {
  const c = document.createElement('canvas'); c.width = w; c.height = h; draw(c.getContext('2d')!, w, h);
  return new THREE.CanvasTexture(c);
};

function buildRoom(world: THREE.Object3D): THREE.Group {
  const room = new THREE.Group(); room.position.x = X0; world.add(room);
  const W = 12, D = 10, H = 3.3;
      // Tatami floor
  const tat = canvasTex(256, 128, (x, w, h)=>{ x.fillStyle = '#C6BC8A'; x.fillRect(0, 0, w, h); x.strokeStyle = 'rgba(130,120,74,.25)'; x.lineWidth = 1;
    for (let y = 4; y < h; y += 4){ x.beginPath(); x.moveTo(0, y); x.lineTo(w, y); x.stroke(); } x.fillStyle = '#5A5440'; x.fillRect(0, 0, w, 4); x.fillRect(0, h - 4, w, 4); });
  tat.wrapS = tat.wrapT = THREE.RepeatWrapping; tat.repeat.set(W/1.8, D/0.9);
  const floor = new THREE.Mesh(new THREE.PlaneGeometry(W, D), new THREE.MeshStandardMaterial({map:tat, roughness:1})); floor.rotation.x = -Math.PI/2; floor.position.y = 0.01; floor.receiveShadow = true; room.add(floor);
  // Shoji side walls over a wooden wainscot
  const wood = plush('#6A503A', '#8E745C', 0.02), darkWood = plush('#3E2C1F', '#5A4636', 0.02), plaster = plush('#E8DFCB', '#E8DFCB', 0);
  const shoji = canvasTex(256, 256, (x, w, h)=>{ x.fillStyle = '#F5E6C4'; x.fillRect(0, 0, w, h); x.strokeStyle = '#5A4632'; x.lineWidth = 6;
    for (let i = 0; i <= 4; i++){ x.beginPath(); x.moveTo(i*w/4, 0); x.lineTo(i*w/4, h); x.stroke(); } for (let j = 0; j <= 6; j++){ x.beginPath(); x.moveTo(0, j*h/6); x.lineTo(w, j*h/6); x.stroke(); } });
  shoji.wrapS = THREE.RepeatWrapping; shoji.repeat.set(D/1.6, 1);
  const shojiMat = new THREE.MeshStandardMaterial({map:shoji, emissive:0xFFE0B0, emissiveMap:shoji, emissiveIntensity:0.45, roughness:1});
  [-1, 1].forEach(sd=>{
    const g = new THREE.Group(); g.position.x = sd*W/2; g.rotation.y = -sd*Math.PI/2; room.add(g);
    const p = new THREE.Mesh(new THREE.PlaneGeometry(D, H - 0.9), shojiMat); p.position.y = 0.45 + (H - 0.9)/2 - 0.2; g.add(p);
    const wain = new THREE.Mesh(new THREE.BoxGeometry(D, 0.45, 0.06), wood); wain.position.y = 0.225; g.add(wain);
    const lint = new THREE.Mesh(new THREE.BoxGeometry(D, 0.5, 0.08), darkWood); lint.position.y = H - 0.25; g.add(lint);
    for (let z = -D/2; z <= D/2 + 0.01; z += D/4){ const post = new THREE.Mesh(new THREE.BoxGeometry(0.16, H, 0.16), darkWood); post.position.set(z, H/2, 0.05); g.add(post); }
  });
  // Back wall: plaster, a snowy-garden window, a tokonoma alcove with a scroll, and the kitchen doorway with a noren
  const back = new THREE.Mesh(new THREE.PlaneGeometry(W, H), plaster); back.position.set(0, H/2, -D/2); back.receiveShadow = true; room.add(back);
  const garden = canvasTex(1024, 460, (x, w, h)=>{
    const sky = x.createLinearGradient(0, 0, 0, h); sky.addColorStop(0, '#B9D1E6'); sky.addColorStop(0.7, '#E4ECF2'); sky.addColorStop(1, '#F2F5F7'); x.fillStyle = sky; x.fillRect(0, 0, w, h);
    // distant ridges
    x.fillStyle = '#C9D4DE'; x.beginPath(); x.moveTo(0, 330); for (let i = 0; i <= 16; i++) x.lineTo(i*w/16, 300 + Math.sin(i*1.7)*18 + (i % 3)*6); x.lineTo(w, h); x.lineTo(0, h); x.fill();
    // Mt. Fuji: concave slopes, flat crater top
    const fuji = () => { x.beginPath(); x.moveTo(40, 360); x.quadraticCurveTo(380, 300, 462, 92); x.lineTo(562, 92); x.quadraticCurveTo(644, 300, 984, 360); x.closePath(); };
    const rock = x.createLinearGradient(0, 92, 0, 360); rock.addColorStop(0, '#6F86A0'); rock.addColorStop(1, '#AFC0D0');
    fuji(); x.fillStyle = rock; x.fill();
    x.save(); fuji(); x.clip();
    x.fillStyle = '#F7F9FC'; x.beginPath(); x.moveTo(300, 92);
    for (let px = 300; px <= 724; px += 16){ const t = (px - 300)/424, base = 182 + 12*Math.sin(t*Math.PI*2.2); x.lineTo(px, base + ((px/16) % 2 ? 26 + 20*Math.abs(Math.sin(px*0.07)) : 0)); }
    x.lineTo(724, 92); x.closePath(); x.fill();
    x.fillStyle = 'rgba(160,180,200,.35)'; for (let k = 0; k < 7; k++){ const px = 470 + k*14; x.beginPath(); x.moveTo(px, 96); x.lineTo(px - 18 - k*4, 185); x.lineTo(px - 10 - k*4, 185); x.closePath(); x.fill(); }
    x.restore();
    // mist band and snowy foreground with conifers and a stone lantern
    const mist = x.createLinearGradient(0, 320, 0, 380); mist.addColorStop(0, 'rgba(242,245,247,0)'); mist.addColorStop(1, 'rgba(242,245,247,.95)'); x.fillStyle = mist; x.fillRect(0, 320, w, 60);
    x.fillStyle = '#FBFCFD'; x.fillRect(0, 380, w, h - 380);
    for (let i = 0; i < 12; i++){ const tx = 30 + i*88 + (i % 2)*20, th = 60 + (i*37 % 50), by = 400 + (i % 3)*8; if (tx > 330 && tx < 690) continue;
      x.fillStyle = i % 3 ? '#5E7A62' : '#4E6A54'; x.beginPath(); x.moveTo(tx, by - th); x.lineTo(tx - 26, by); x.lineTo(tx + 26, by); x.fill();
      x.fillStyle = '#FFFFFF'; x.beginPath(); x.moveTo(tx, by - th); x.lineTo(tx - 11, by - th*0.58); x.lineTo(tx + 11, by - th*0.58); x.fill(); }
    x.fillStyle = '#9BA19C'; x.fillRect(820, 352, 20, 40); x.fillStyle = '#FFE6B0'; x.fillRect(817, 357, 26, 13); x.fillStyle = '#FFFFFF'; x.fillRect(812, 346, 36, 7);
  });
  const win = new THREE.Mesh(new THREE.PlaneGeometry(3.6, 1.62), new THREE.MeshBasicMaterial({map:garden})); win.position.set(-1.55, 1.75, -D/2 + 0.02); room.add(win);
  ([[-1.55, 2.6, 3.8, 0.12], [-1.55, 0.9, 3.8, 0.12]] as const).forEach(([x, y, w, h])=>{ const b = new THREE.Mesh(new THREE.BoxGeometry(w, h, 0.1), darkWood); b.position.set(x, y, -D/2 + 0.05); room.add(b); });
  [-3.35, 0.25].forEach(x=>{ const b = new THREE.Mesh(new THREE.BoxGeometry(0.1, 1.8, 0.1), darkWood); b.position.set(x, 1.75, -D/2 + 0.05); room.add(b); });
  const toko = new THREE.Mesh(new THREE.BoxGeometry(2.2, 0.18, 0.9), wood); toko.position.set(4.2, 0.09, -D/2 + 0.45); room.add(toko);
  const tokoPost = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.09, H, 8), plush('#7A5A3E', '#A0805E', 0.03)); tokoPost.position.set(3.05, H/2, -D/2 + 0.9); room.add(tokoPost);
  const scroll = canvasTex(128, 320, (x, w, h)=>{ x.fillStyle = '#EFE7D4'; x.fillRect(0, 0, w, h); x.fillStyle = '#8A7A5E'; x.fillRect(0, 0, w, 16); x.fillRect(0, h - 16, w, 16);
    x.fillStyle = '#4A4A48'; x.beginPath(); x.moveTo(18, 140); x.lineTo(64, 70); x.lineTo(110, 140); x.fill(); x.fillStyle = '#EFE7D4'; x.beginPath(); x.moveTo(50, 92); x.lineTo(64, 70); x.lineTo(78, 92); x.fill();
    x.fillStyle = '#2A2826'; x.font = '700 64px "Hiragino Mincho ProN", "Yu Mincho", "Noto Serif CJK JP", serif'; x.textAlign = 'center'; x.fillText('湯', 64, 236); x.fillStyle = '#B23A2E'; x.fillRect(84, 262, 16, 16); });
  const sc = new THREE.Mesh(new THREE.PlaneGeometry(0.62, 1.55), new THREE.MeshStandardMaterial({map:scroll, roughness:1})); sc.position.set(4.3, 1.75, -D/2 + 0.03); room.add(sc);
  const vase = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.11, 0.32, 14), plush('#3E4C6E', '#6E7EA0', 0.05)); vase.position.set(4.9, 0.34, -D/2 + 0.45); room.add(vase);
  const twig = new THREE.Mesh(new THREE.CylinderGeometry(0.01, 0.015, 0.7, 5), darkWood); twig.position.set(4.95, 0.75, -D/2 + 0.45); twig.rotation.z = -0.3; room.add(twig);
  ([[5.02, 0.9], [4.88, 0.72], [5.1, 1.02]] as const).forEach(([x, y])=>{ const f = new THREE.Mesh(S(0.04, 8, 6), plush('#E9A0AE', '#FFD6DE', 0.1)); f.position.set(x, y, -D/2 + 0.45); room.add(f); });
  const doorway = new THREE.Mesh(new THREE.PlaneGeometry(1.7, 2.2), plush('#231B15', '#231B15', 0)); doorway.position.set(-4.75, 1.1, -D/2 + 0.02); room.add(doorway);
  const noren = canvasTex(256, 160, (x, w, h)=>{ x.fillStyle = '#2E3E66'; x.fillRect(0, 0, w, h); x.fillStyle = '#1E2A48'; x.fillRect(84, 30, 4, h); x.fillRect(170, 30, 4, h);
    x.fillStyle = '#F4F1EA'; x.font = '700 48px "Hiragino Mincho ProN", "Yu Mincho", "Noto Serif CJK JP", serif'; x.textAlign = 'center'; x.textBaseline = 'middle'; x.fillText('お食事処', w/2, 72); });
  const nr = new THREE.Mesh(new THREE.PlaneGeometry(1.7, 1.05), new THREE.MeshStandardMaterial({map:noren, roughness:1, side:THREE.DoubleSide})); nr.position.set(-4.75, 1.68, -D/2 + 0.08); room.add(nr);
  // Ceiling with beams and paper lanterns
  const ceil = new THREE.Mesh(new THREE.PlaneGeometry(W, D), plush('#4A3626', '#4A3626', 0)); ceil.rotation.x = Math.PI/2; ceil.position.y = H; room.add(ceil);
  for (let x = -W/2 + 1.5; x < W/2; x += 3){ const bm = new THREE.Mesh(new THREE.BoxGeometry(0.22, 0.2, D), darkWood); bm.position.set(x, H - 0.1, 0); room.add(bm); }
  const paper = new THREE.MeshStandardMaterial({color:0xFFF4DE, emissive:0xFFD9A0, emissiveIntensity:0.9, roughness:1});
  ([[-3.5, -0.4], [0, -3.4], [3.5, -0.4], [-3.5, 2.8], [3.5, 2.8]] as const).forEach(([x, z])=>{
    const l = new THREE.Mesh(S(0.28, 18, 12), paper); l.scale.set(1, 1.25, 1); l.position.set(x, 2.55, z); room.add(l);
    const cord = new THREE.Mesh(new THREE.CylinderGeometry(0.01, 0.01, 0.4, 4), darkWood); cord.position.set(x, H - 0.2, z); room.add(cord);
    [-0.33, 0.33].forEach(o=>{ const rim = new THREE.Mesh(new THREE.CylinderGeometry(0.16, 0.16, 0.04, 14), darkWood); rim.position.set(x, 2.55 + o, z); room.add(rim); });
  });
  const roomLight = new THREE.PointLight(0xFFD49A, 0.6 * LEGACY, 16, 2); roomLight.position.set(0, 2.5, 0); room.add(roomLight);
  // Low tables and floor cushions
  const tableMat = plush('#4A2F20', '#6E4E3A', 0.03), cushion = plush('#3D4D78', '#6A7AA6', 0.05);
  function lowTable(x: number, z: number){
    const top = mk(new THREE.BoxGeometry(1.8, 0.08, 1.0), tableMat); top.position.set(x, 0.48, z); room.add(top);
    ([[-0.78, -0.4], [0.78, -0.4], [-0.78, 0.4], [0.78, 0.4]] as const).forEach(([dx, dz])=>{ const leg = mk(new THREE.BoxGeometry(0.08, 0.44, 0.08), tableMat); leg.position.set(x + dx, 0.22, z + dz); room.add(leg); });
  }
  function zabuton(x: number, z: number){ const c = mk(new THREE.BoxGeometry(1.0, 0.09, 1.0), cushion); c.position.set(x, 0.05, z); room.add(c); }
  lowTable(0, 0.2); zabuton(0, -1.6);
  DINER_SEATS.forEach(d => { lowTable(d.seat.x - X0, d.seat.z + 1.8); zabuton(d.seat.x - X0, d.seat.z); });
  return room;
}

export interface Diner extends QuietCapy { diner: true; dishSpot: THREE.Vector3; nextEat: number }

export interface Ryokan {
  room: THREE.Group;
  diners: Diner[];
  /** 식당 안에 있는지. */
  indoors: boolean;
  /** 실내에서는 숨기는 내리는 눈. */
  snow: THREE.Object3D | null;
}

export function buildRyokan(world: THREE.Object3D, R: QuietRules, snow: THREE.Object3D | null = null): Ryokan {
  const room = buildRoom(world);
  const tones: FurTone[] = ['classic', 'honey', 'cocoa'];
  const diners = DINER_SEATS.map((d, i) => {
    const a = asQuiet(makeAgent(world, false, tones[(i + 1) % 3]!)) as Diner;
    setHead(a, (['towel', 'none', 'yuzu'] as const)[i]!);
    a.mode = 'indoors'; a.diner = true; a.unavailable = true; a.pos.copy(d.seat); a.facing = a.targetFacing = 0; a.dishSpot = d.dishSpot; a.nextEat = rand(1, 8);
    R.agents.push(a);
    return a;
  });
  return { room, diners, indoors: false, snow };
}

/** 식당 손님은 12~24초마다 아무 메뉴나 하나 시켜 먹는다. */
export function dinerThink(Y: Ryokan, world: THREE.Object3D, t: number): void {
  const keys = Object.keys(MENUS.ryokan) as DishId[];
  for (const a of Y.diners) {
    if (!a.eating && t > a.nextEat) { serve(a, keys[Math.floor(Math.random() * keys.length)]!, a.dishSpot, world, t); a.nextEat = t + rand(12, 24); }
  }
}

/** 문으로 걸어가 들어가기 시작 (도착하면 enterRoom). 이미 문으로 가는 중이면 취소. */
export function goIn(R: QuietRules, Y: Ryokan, a: QuietCapy, t: number): void {
  if (Y.indoors) return;
  if (a.riding || a.hop) { hopDown(a, t, R.nav, () => goIn(R, Y, a, t), R.landing); return; }
  if (a.enterAfter && a.mode === 'goto') { a.enterAfter = false; a.mode = 'idle'; a.wander = null; return; }
  releaseSlot(a); releaseWait(a); a.settled = false; a.napping = false; a.emote = null;
  a.mode = 'goto'; a.wander = DOOR.clone(); a.enterAfter = true;
}

export function enterRoom(R: QuietRules, Y: Ryokan, a: QuietCapy, cam: FollowCamera, t: number): void {
  Y.indoors = true;
  if (Y.snow) Y.snow.visible = false;
  shakeOff(a, t, R.nav, undefined, R.landing);
  a.mode = 'indoors'; a.enterAfter = false; a.pos.copy(ROOM.seat); a.facing = a.targetFacing = 0; a.bathAmt = 0; a.path = null; a.c.root.position.copy(ROOM.seat);
  cam.indoor = true; cam.yaw = 0; cam.pitch = 0.3; cam.dist = 7.4; cam.target.set(ROOM.seat.x, 0.6, ROOM.seat.z + 0.8); cam.snap();
}

/** 식당에서 나와 문 앞에 선다. 바로 다시 들어가지 않게 문에서 3m 멀어질 때까지 문 확인을 쉰다. */
export function exitRoom(Y: Ryokan, a: QuietCapy, cam: FollowCamera, aspect: number): void {
  Y.indoors = false;
  if (Y.snow) Y.snow.visible = true;
  cleanupEating(a); if (a.dish) { a.dish.group.parent?.remove(a.dish.group); a.dish = null; }
  a.mode = 'idle'; a.pos.copy(DOOR); a.facing = a.targetFacing = 0; a.c.root.position.copy(DOOR);
  cam.indoor = false; cam.yaw = 0.85; cam.pitch = 0.32; cam.dist = aspect < 0.9 ? 23 : 17; cam.target.set(DOOR.x, 0.6, DOOR.z); cam.snap();
  a.doorGuard = true;
}

/** 내 자리 상에 음식을 차린다. */
export function orderDish(a: QuietCapy, key: DishId, world: THREE.Object3D, t: number): void {
  serve(a, key, ROOM.dishSpot, world, t);
}

/** 매 프레임: 문 앞에 서면 들어간다 (탕에 있을 때는 아님). 프로토타입 3979–3983. */
export function doorCheck(R: QuietRules, Y: Ryokan, a: QuietCapy, cam: FollowCamera, t: number): void {
  if (Y.indoors) return;
  const dd = Math.hypot(a.pos.x - DOOR.x, a.pos.z - DOOR.z);
  if (a.doorGuard) { if (dd > 3) a.doorGuard = false; }
  else if (dd < 1.4 && a.mode !== 'bathe' && a.bathAmt < 0.1) { releaseSlot(a); releaseWait(a); enterRoom(R, Y, a, cam, t); }
}

/** 목욕탕 정면을 탭하면 문으로 간다. */
export const isDoorTap = (x: number, z: number): boolean => Math.abs(x) < 4.6 && z < -10.2 && z > -16.2;
